import { dramas } from "@/mocks/dramas";
import type { Drama } from "@/mocks/types";

export const ALL = "全部";

export interface FilterState {
  region: string;
  yearRange: string;
  ratingRange: string;
  sort: "updated" | "hot" | "rating" | "year";
}

export const defaultFilters: FilterState = {
  region: ALL,
  yearRange: ALL,
  ratingRange: ALL,
  sort: "updated",
};

export const yearOptions = [
  { label: ALL, value: ALL },
  { label: "2025", value: "2025" },
  { label: "2023-2024", value: "2023-2024" },
  { label: "2020-2022", value: "2020-2022" },
  { label: "2015-2019", value: "2015-2019" },
];

export const ratingOptions = [
  { label: ALL, value: ALL },
  { label: "9.0 分以上", value: "9" },
  { label: "8.5 - 8.9", value: "8.5" },
  { label: "8.0 - 8.4", value: "8" },
  { label: "8.0 以下", value: "below8" },
];

export const sortOptions = [
  { label: "按更新日期", value: "updated" },
  { label: "按热门度", value: "hot" },
  { label: "按评分", value: "rating" },
  { label: "按首播年份", value: "year" },
] as const;

const matchYear = (year: number, range: string) => {
  if (range === ALL) return true;
  if (range === "2025") return year >= 2025;
  if (range === "2023-2024") return year >= 2023 && year <= 2024;
  if (range === "2020-2022") return year >= 2020 && year <= 2022;
  if (range === "2015-2019") return year >= 2015 && year <= 2019;
  return true;
};

const matchRating = (rating: number, range: string) => {
  if (range === ALL) return true;
  if (range === "9") return rating >= 9;
  if (range === "8.5") return rating >= 8.5 && rating < 9;
  if (range === "8") return rating >= 8 && rating < 8.5;
  if (range === "below8") return rating < 8;
  return true;
};

const sorters: Record<FilterState["sort"], (a: Drama, b: Drama) => number> = {
  updated: (a, b) => b.updatedAt.localeCompare(a.updatedAt),
  hot: (a, b) => b.hot - a.hot,
  rating: (a, b) => b.rating - a.rating,
  year: (a, b) => b.year - a.year,
};

export function filterDramas(list: Drama[], filters: FilterState): Drama[] {
  return list
    .filter((item) => (filters.region === ALL ? true : item.region === filters.region))
    .filter((item) => matchYear(item.year, filters.yearRange))
    .filter((item) => matchRating(item.rating, filters.ratingRange))
    .sort(sorters[filters.sort]);
}

export function searchDramas(keyword: string): Drama[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  const tokens = q.split(/\s+/).filter(Boolean);
  return dramas.filter((item) => {
    const haystack = [
      item.title,
      item.originalTitle,
      item.tagline,
      item.summary,
      item.director,
      item.types.join(" "),
      item.tags.join(" "),
      item.cast.join(" "),
      String(item.year),
    ]
      .join(" ")
      .toLowerCase();
    return tokens.some((token) => haystack.includes(token));
  });
}

export function getRelated(item: Drama, limit = 3): Drama[] {
  const scored = dramas
    .filter((d) => d.id !== item.id)
    .map((d) => {
      const sharedTags = d.tags.filter((tag) => item.tags.includes(tag)).length;
      const sameCategory = d.category === item.category ? 2 : 0;
      const sharedTypes = d.types.filter((t) => item.types.includes(t)).length;
      return { item: d, score: sharedTags * 2 + sameCategory + sharedTypes };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || b.item.hot - a.item.hot);
  return scored.slice(0, limit).map((entry) => entry.item);
}

export const formatDate = (iso: string) => {
  const [y, m, d] = iso.split("-");
  return `${y}.${m}.${d}`;
};

export const PAGE_SIZE = 6;